import type { FileError, FileWithPath } from "react-dropzone";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { FileExtractionStatus, JobStatus } from "../models/jobStatus";

interface InitialState {
  isProcessing: boolean;
  batchId: string | undefined;
  jobStatus: JobStatus[];
  currentProcess: number;
  isBatchFailure: boolean;
  acceptedFiles: (FileWithPath & { status: FileExtractionStatus })[];
  isDownloadEnabled: boolean;
  isFileUploaded: boolean;
  rejectedFiles: { file: File; errors: FileError[] }[];
}

interface StoreActions {
  setBatchId: (batchId: string | undefined) => void;
  setIsProcessing: (value: boolean) => void;
  setJobStatus: (jobStatus: JobStatus[]) => void;
  setCurrentProcess: (currentProcess: number) => void;
  setIsBatchFailure: (value: boolean) => void;
  setAcceptedFiles: (
    value: (FileWithPath & { status: FileExtractionStatus })[]
  ) => void;
  setIsDownloadEnabled: (value: boolean) => void;
  setIsFileUploaded: (value: boolean) => void;
  resetState: () => void;
  setRejectedFiles: (
    rejectedFiles: { file: File; errors: FileError[] }[]
  ) => void;
}

const initialState: InitialState = {
  isProcessing: false,
  batchId: undefined,
  jobStatus: [],
  currentProcess: 0,
  isBatchFailure: false,
  acceptedFiles: [],
  isDownloadEnabled: false,
  isFileUploaded: false,
  rejectedFiles: [],
};

const useBatchProcessStore = create<InitialState & StoreActions>()(
  devtools((set) => ({
    ...initialState,
    setIsProcessing: (isProcessing: boolean) => {
      set({ isProcessing });
    },

    setBatchId: (batchId: string | undefined) => {
      set({ batchId });
    },

    setJobStatus: (jobStatus: JobStatus[]) => {
      set({ jobStatus });
    },

    setCurrentProcess: (currentProcess: number) => {
      set({ currentProcess });
    },

    setIsBatchFailure: (isBatchFailure: boolean) => {
      set({ isBatchFailure });
    },

    setAcceptedFiles: (
      acceptedFiles: (FileWithPath & { status: FileExtractionStatus })[]
    ) => {
      set({ acceptedFiles });
    },

    setIsFileUploaded: (isFileUploaded: boolean) => {
      set({ isFileUploaded });
    },

    setIsDownloadEnabled: (isDownloadEnabled: boolean) => {
      set({ isDownloadEnabled });
    },

    setRejectedFiles: (
      rejectedFiles: { file: File; errors: FileError[] }[]
    ) => {
      set({ rejectedFiles });
    },

    resetState: () => {
      set({
        isProcessing: false,
        batchId: undefined,
        jobStatus: [],
        currentProcess: 0,
        isBatchFailure: false,
        isFileUploaded: false,
        isDownloadEnabled: false,
      });
    },
  }))
);

export default useBatchProcessStore;
